const imageCache = new Map<string, HTMLImageElement>();
const drawSourceCache = new Map<string, HTMLCanvasElement>();

function getOrLoadImage(imageUrl: string, resolveImageSrc: (imageUrl: string) => string): HTMLImageElement {
  let image = imageCache.get(imageUrl);
  if (!image) {
    image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const context = canvas.getContext('2d');
      if (context && image) {
        context.drawImage(image, 0, 0, 64, 64);
        drawSourceCache.set(imageUrl, canvas);
      }
    };
    image.src = resolveImageSrc(imageUrl);
    imageCache.set(imageUrl, image);
  }
  return image;
}

export function preloadConfettiImages(imageUrls: string[], resolveImageSrc: (imageUrl: string) => string): Promise<void> {
  const urls = (imageUrls || []).filter((imageUrl) => !!imageUrl);
  return Promise.all(urls.map((imageUrl) => new Promise<void>((resolve) => {
    if (drawSourceCache.has(imageUrl)) {
      resolve();
      return;
    }
    const image = getOrLoadImage(imageUrl, resolveImageSrc);
    if (image.complete) {
      resolve();
      return;
    }
    image.addEventListener('load', () => resolve(), { once: true });
    image.addEventListener('error', () => resolve(), { once: true });
  }))).then(() => undefined);
}

export function linearTween(currentTime: number, currentValue: number, targetValue: number, duration: number): number {
  return currentValue + (targetValue - currentValue) * (duration > 0 ? currentTime / duration : 1);
}

export function createImageConfettiDrawShape(imageUrls: string[], resolveImageSrc: (imageUrl: string) => string): (this: any, context: CanvasRenderingContext2D) => void {
  const urls = (imageUrls || []).filter((imageUrl) => !!imageUrl);
  urls.forEach((imageUrl) => getOrLoadImage(imageUrl, resolveImageSrc));

  return function (this: any, context: CanvasRenderingContext2D): void {
    const size = this.radius || 10;
    const minAngularSpin = 0.06;
    if (Math.abs(this.angularSpin) < minAngularSpin) {
      this.angularSpin = this.angularSpin < 0 ? -minAngularSpin : minAngularSpin;
    }
    const slowedAngle = this.angle * 0.35;
    context.setTransform(
      Math.cos(slowedAngle), Math.sin(slowedAngle),
      -Math.sin(slowedAngle), Math.cos(slowedAngle),
      this.x, this.y
    );
    if (this._flips === undefined) {
      this._flips = Math.random() < 0.3;
    }
    if (this._flips) {
      context.scale(1, 1 + (this.rotateY - 1) * 0.4);
    }
    if (this._fallTimingApplied) {
      this.vy *= this._frictionAdjust;
    } else {
      this._fallTimingApplied = true;
      this._frictionAdjust = (0.98 + Math.random() * 0.015) / 0.99;
      this.vy = Math.random() * 6 - 3;
    }
    if (this._imageIndex === undefined) {
      this._imageIndex = urls.length > 0 ? Math.floor(Math.random() * urls.length) : 0;
    }
    const drawSource = drawSourceCache.get(urls[this._imageIndex]);
    if (drawSource) {
      const imgSize = size * 3.5;
      context.drawImage(drawSource, -imgSize / 2, -imgSize / 2, imgSize, imgSize);
    } else {
      context.beginPath();
      context.arc(0, 0, size / 2, 0, 2 * Math.PI);
      context.fill();
    }
  };
}